import { api } from './http';
import type {
  FormField,
  StudyForm,
  StudyFormsFilters,
  StudyFormsResponse,
  StudyFormWithFields,
} from '@/types/studyForms';
import { API_ENDPOINTS } from '@/config/constants';

// Study Forms API functions
export async function getStudyForms(
  filters?: StudyFormsFilters
): Promise<StudyFormsResponse> {
  const params = new URLSearchParams();

  if (filters?.page) {
    params.append('page', filters.page.toString());
  }

  if (filters?.per_page) {
    params.append('per_page', filters.per_page.toString());
  }

  if (filters?.order) {
    params.append('order', filters.order);
  }

  if (filters?.order_direction) {
    params.append('order_direction', filters.order_direction);
  }

  const url = `${API_ENDPOINTS.STUDY_FORMS}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);

  // Extract data from the response structure
  if (
    response.data &&
    response.data.data &&
    Array.isArray(response.data.data) &&
    response.data.pagination
  ) {
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  }

  // Fallback for direct array response
  if (Array.isArray(response.data)) {
    return {
      data: response.data,
      pagination: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 1,
        total_count: response.data.length,
        per_page: response.data.length,
      },
    };
  }

  return {
    data: [],
    pagination: {
      current_page: 1,
      next_page: null,
      prev_page: null,
      total_pages: 1,
      total_count: 0,
      per_page: 10,
    },
  };
}

// Convert StudyForm from API to StudyFormWithFields for frontend use
export function convertStudyFormToWithFields(
  studyForm: StudyForm
): StudyFormWithFields {
  let fields: FormField[] = [];
  let fieldCount = 0;

  try {
    // Parse JSON schema to extract fields
    if (studyForm.json_schema && typeof studyForm.json_schema === 'object') {
      const schema = studyForm.json_schema as Record<string, unknown>;

      // Handle new structure with fieldsArray
      if (Array.isArray(schema.fieldsArray)) {
        type SchemaFieldItem = {
          dataPoint: string;
          subForm?: unknown;
          input?: Record<string, unknown>;
          question?: Record<string, unknown>;
        };

        fields = (schema.fieldsArray as SchemaFieldItem[])
          .filter(fieldItem => !fieldItem.subForm)
          .map(fieldItem => {
            // Map new structure field types to our FormField types
            let fieldType: FormField['type'] = 'text';

            if (fieldItem.input && typeof fieldItem.input === 'object') {
              const inputType = (fieldItem.input as Record<string, unknown>)
                .type;

              if (inputType === 'text') {
                const questionList = fieldItem.question
                  ? (fieldItem.question as Record<string, unknown>).list
                  : undefined;
                if (Array.isArray(questionList)) {
                  fieldType = 'select';
                } else {
                  fieldType = 'text';
                }
              } else if (inputType === 'number') {
                fieldType = 'number';
              } else if (inputType === 'date') {
                fieldType = 'date';
              } else if (inputType === 'time') {
                fieldType = 'time';
              } else if (inputType === 'radio') {
                fieldType = 'radio';
              } else {
                fieldType = 'text';
              }
            }

            const questionLabel = fieldItem.question
              ? (fieldItem.question as Record<string, unknown>).label
              : undefined;
            const questionList = fieldItem.question
              ? (fieldItem.question as Record<string, unknown>).list
              : undefined;
            const inputDisplay = fieldItem.input
              ? (fieldItem.input as Record<string, unknown>).display
              : undefined;
            const inputFormat = fieldItem.input
              ? (fieldItem.input as Record<string, unknown>).format
              : undefined;

            return {
              dataPoint: fieldItem.dataPoint,
              label:
                typeof questionLabel === 'string'
                  ? questionLabel
                  : fieldItem.dataPoint,
              type: fieldType,
              required: false, // Will be determined by business logic if needed
              allowedValues: Array.isArray(questionList)
                ? (questionList as string[])
                : Array.isArray(inputDisplay)
                  ? (inputDisplay as string[])
                  : undefined,
              format: typeof inputFormat === 'string' ? inputFormat : undefined,
            };
          });

        fieldCount = fields.length;
      }
      // Fallback for old structure with properties
      else if (schema.properties && typeof schema.properties === 'object') {
        const properties = schema.properties as Record<string, unknown>;
        const requiredFields = Array.isArray(schema.required)
          ? (schema.required as string[])
          : [];

        fields = Object.entries(properties).map(
          ([fieldName, fieldDef]: [string, unknown]) => {
            const def =
              fieldDef && typeof fieldDef === 'object'
                ? (fieldDef as Record<string, unknown>)
                : {};
            // Map JSON schema field types to our FormField types
            let fieldType: FormField['type'] = 'text';
            if (def.type === 'string') {
              if (def.format === 'date') {
                fieldType = 'date';
              } else if (def.format === 'time') {
                fieldType = 'time';
              } else if (def.enum) {
                fieldType = 'select';
              } else {
                fieldType = 'text';
              }
            } else if (
              def.type === 'number' ||
              def.type === 'integer'
            ) {
              fieldType = 'number';
            } else if (def.type === 'boolean') {
              fieldType = 'radio';
            }

            return {
              dataPoint: fieldName,
              label: typeof def.title === 'string' ? def.title : fieldName,
              type: fieldType,
              required: requiredFields.includes(fieldName),
              allowedValues: Array.isArray(def.enum)
                ? (def.enum as string[])
                : undefined,
              format: typeof def.format === 'string' ? def.format : undefined,
            };
          }
        );

        fieldCount = fields.length;
      }
    }
  } catch {
    fields = [];
    fieldCount = 0;
  }

  return {
    ...studyForm,
    fields,
    fieldCount,
  };
}

// Convert array of StudyForms to StudyFormWithFields
export function convertStudyFormsToWithFields(
  studyForms: StudyForm[]
): StudyFormWithFields[] {
  return studyForms.map(convertStudyFormToWithFields);
}

// Get study forms with parsed fields for frontend use
export async function getStudyFormsWithFields(
  filters?: StudyFormsFilters
): Promise<{
  data: StudyFormWithFields[];
  pagination: StudyFormsResponse['pagination'];
}> {
  const response = await getStudyForms(filters);

  return {
    data: convertStudyFormsToWithFields(response.data),
    pagination: response.pagination,
  };
}
